import { IntegrationConfig, FeatureBlock, FeatureKey } from './config.model';

/**
 * Pure helpers for the Integration page's setup gating. Kept React-free so they
 * can be unit-tested without rendering `integration.page.tsx` (whose component
 * tree drags in Auth/Lottie/canvas deps that jsdom can't construct).
 */

/**
 * Whether the integration is fully wired up — used to unlock the Matching/Features
 * tabs and to keep the Save button visible once everything is in place:
 *   - a feature is enabled (or, for legacy v1 configs, `isEnabled`), AND
 *   - at least one allowed domain, AND
 *   - at least one domain has a verified live connection (`connectedAt`).
 */
export const computeIsSetupComplete = (integration: IntegrationConfig): boolean => {
    const hasAnyDomainConnected = !!(
        integration.domainStatus &&
        Object.values(integration.domainStatus).some(s => !!s?.connectedAt)
    );
    return isIntegrationEnabled(integration)
        && (integration.allowedDomains?.length ?? 0) > 0
        && hasAnyDomainConnected;
};

/**
 * Whether the website is connected: a domain is allowed and the SDK has reported
 * from at least one of them.
 *
 * Separate from `computeIsSetupComplete`, which also needs a feature switched
 * on. Connecting the website is general — the visualizer, the Shopping Assistant
 * and anything after them all run from the same snippet — and is done on Home.
 * Which features run is each workspace's own business, so a vendor can connect
 * first and decide later. The SDK's config request records the visit whenever
 * the domain is allowed, feature or not, so this never deadlocks.
 */
export const isWebsiteConnected = (integration: IntegrationConfig): boolean =>
    (integration.allowedDomains?.length ?? 0) > 0
    && !!integration.domainStatus
    && Object.values(integration.domainStatus).some(s => !!s?.connectedAt);

/**
 * Whether the integration is on, as the master switch should display it.
 *
 * For v2 configs the features map is the source of truth: `isEnabled` is a
 * DERIVED mirror, recomputed from the features on every save (see the v1 shim in
 * integration.page.tsx's handleSave) so the backend's `/config` master gate flips
 * true when ANY feature is on. Reading `isEnabled` directly in the UI therefore
 * makes the switch show a value that the next save will overwrite.
 */
export const isIntegrationEnabled = (integration: IntegrationConfig): boolean =>
    integration.features
        ? Object.values(integration.features).some(f => !!f?.enabled)
        : !!integration.isEnabled;

/**
 * Apply the master enable switch, returning a new config.
 *
 * Writing straight to `isEnabled` looks like it works but silently doesn't on v2
 * configs: the save path recomputes `isEnabled` from the features map, so with
 * every feature off the flag lands back on `false` and the switch springs off
 * again. That also deadlocked setup — the per-feature toggles live on the
 * Features tab, which stays locked until `computeIsSetupComplete` sees a feature
 * enabled, so there was no reachable control that could ever enable one.
 *
 * Turning ON with nothing enabled turns on `visualizer`: it is the feature every
 * vendor starts with, it is what the switch's own description promises ("the
 * simulator button will appear on your product pages"), and leaving all three off
 * would be indistinguishable from OFF. If some feature is already on, ON is a
 * no-op. Turning OFF disables every feature.
 */
export const applyIntegrationEnabled = (
    integration: IntegrationConfig,
    enabled: boolean,
): IntegrationConfig => {
    // Legacy v1 config — no features map, `isEnabled` is genuinely the state.
    if (!integration.features) {
        return { ...integration, isEnabled: enabled };
    }

    const features = { ...integration.features } as Record<FeatureKey, FeatureBlock>;
    if (enabled) {
        if (!isIntegrationEnabled(integration) && features.visualizer) {
            features.visualizer = { ...features.visualizer, enabled: true };
        }
    } else {
        (Object.keys(features) as FeatureKey[]).forEach(key => {
            if (features[key]) features[key] = { ...features[key], enabled: false };
        });
    }

    return {
        ...integration,
        features,
        // Keep the legacy mirror in step immediately, so the v1 shim and anything
        // still reading `isEnabled` agree with the features map before the save
        // recomputes it anyway.
        isEnabled: Object.values(features).some(f => !!f?.enabled),
    };
};
